import { FormEvent, ReactNode, useState } from "react"
import { Head, Link, router, useForm } from "@inertiajs/react"
import { AdminShell } from "@/components/AdminShell"
import { Badge } from "@/components/ui/badge"
import { DescriptionDetails, DescriptionList, DescriptionTerm } from "@/components/catalyst/description-list"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Checkbox } from "@/components/ui/checkbox"
import { Select } from "@/components/ui/select"
import { UserPrograms, type MembershipRow } from "@/components/admin/UserPrograms"

type Role = "user" | "instructor" | "admin"

type UserDetail = {
  id: number
  email: string
  name: string | null
  admin: boolean
  instructor: boolean
  role: Role
  translator: boolean
  translator_locales: string[]
  disabled: boolean
  disabled_at: string | null
  timezone: string | null
  created_at: string
  updated_at: string
}

const ROLES: { value: Role; label: string; help: string }[] = [
  { value: "user", label: "User", help: "Studies with the app. No admin access." },
  {
    value: "instructor",
    label: "Instructor",
    help: "Can manage official decks, quizzes, and practice exams. Can't see users, feedback, or ratings.",
  },
  { value: "admin", label: "Admin", help: "Full access, including users, feedback, and ratings." },
]

export default function AdminUserShow({
  user,
  is_self,
  programs,
  memberships,
  taught_program_ids,
}: {
  user: UserDetail
  is_self: boolean
  programs: { id: number; name: string }[]
  memberships: MembershipRow[]
  taught_program_ids: number[]
}) {
  const [confirmingDisable, setConfirmingDisable] = useState(false)
  const form = useForm({ user: { role: user.role } })
  const title = user.name ?? user.email
  const selectedRole = ROLES.find((r) => r.value === form.data.user.role)

  const saveRole = (e: FormEvent) => {
    e.preventDefault()
    form.patch(`/admin/users/${user.id}`, { preserveScroll: true })
  }

  const disable = () => {
    setConfirmingDisable(false)
    router.patch(`/admin/users/${user.id}/disable`, {}, { preserveScroll: true })
  }

  const enable = () => router.patch(`/admin/users/${user.id}/enable`, {}, { preserveScroll: true })

  return (
    <>
      <Head title={title}>
        <meta name="description" content={`User detail for ${user.email}.`} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={`User detail for ${user.email}.`} />
      </Head>
      <AdminShell>
        <div className="border-b border-hairline pb-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h1>{title}</h1>
              <p className="mt-1">{user.name ? `${user.email} · ` : ""}User #{user.id}</p>
            </div>
            <div className="flex items-center gap-2">
              {user.disabled && <Badge tone="signal">Disabled</Badge>}
              {user.admin && <Badge tone="accent">Admin</Badge>}
              {user.instructor && <Badge tone="neutral">Instructor</Badge>}
              <Link href="/admin/users" className="text-sm text-ink-muted no-underline hover:text-ink-display">
                Back to users
              </Link>
            </div>
          </div>
        </div>

        <DescriptionList className="mt-6 max-w-2xl">
          <DetailRow label="Name">{user.name ?? "—"}</DetailRow>
          <DetailRow label="Email">{user.email}</DetailRow>
          <DetailRow label="Role">{ROLES.find((r) => r.value === user.role)?.label}</DetailRow>
          <DetailRow label="Status">
            {user.disabled ? `Disabled ${formatDateTime(user.disabled_at!)}` : "Active"}
          </DetailRow>
          <DetailRow label="Timezone">{user.timezone ?? "—"}</DetailRow>
          <DetailRow label="Created">{formatDateTime(user.created_at)}</DetailRow>
          <DetailRow label="Updated">{formatDateTime(user.updated_at)}</DetailRow>
        </DescriptionList>

        {is_self && (
          <p className="callout mt-8 max-w-md text-sm">
            This is your own account, so you can&rsquo;t change your role or disable it.
          </p>
        )}

        <form onSubmit={saveRole} className="mt-8 max-w-md space-y-3">
          <h2>Change role</h2>
          <div className="space-y-2">
            <label htmlFor="user-role">Role</label>
            <Select
              id="user-role"
              disabled={is_self}
              value={form.data.user.role}
              onChange={(e) => form.setData("user", { role: e.target.value as Role })}
            >
              {ROLES.map((role) => (
                <option key={role.value} value={role.value}>
                  {role.label}
                </option>
              ))}
            </Select>
            <p className="text-xs text-ink-muted">{selectedRole?.help}</p>
          </div>
          <Button type="submit" disabled={is_self || form.processing || form.data.user.role === user.role}>
            Save role
          </Button>
        </form>

        <UserPrograms
          userId={user.id}
          userEmail={user.email}
          isInstructor={user.instructor}
          programs={programs}
          memberships={memberships}
          taughtProgramIds={taught_program_ids}
        />

        <TranslatorPanel user={user} />

        {!is_self && (
          <section className="mt-10 max-w-md space-y-3" aria-label="Account status">
            <h2>Account</h2>
            {user.disabled ? (
              <>
                <p>This account is disabled. They can&rsquo;t sign in, with a password or Google.</p>
                <Button type="button" onClick={enable}>
                  Enable account
                </Button>
              </>
            ) : (
              <>
                <p>Disabling signs them out everywhere and stops them from signing in. Their data is kept.</p>
                <Button type="button" variant="danger" onClick={() => setConfirmingDisable(true)}>
                  Disable account
                </Button>
              </>
            )}
          </section>
        )}

        <Dialog open={confirmingDisable} onOpenChange={setConfirmingDisable}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Disable {user.email}?</DialogTitle>
              <DialogDescription>
                They&rsquo;ll be signed out right away and won&rsquo;t be able to sign in until you enable the account again.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setConfirmingDisable(false)}>
                Cancel
              </Button>
              <Button type="button" variant="danger" onClick={disable}>
                Disable account
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </AdminShell>
    </>
  )
}

function DetailRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <DescriptionTerm>{label}</DescriptionTerm>
      <DescriptionDetails className="min-w-0 break-words">{children}</DescriptionDetails>
    </>
  )
}

function formatDateTime(iso: string) {
  return new Date(iso).toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })
}

const TRANSLATABLE = [
  { code: "es-419", name: "Español (Latinoamérica)" },
  { code: "fr-CA", name: "Français (Canada)" },
]

/** Lets an admin make someone a translator and choose the languages they review. */
function TranslatorPanel({ user }: { user: UserDetail }) {
  const form = useForm({ translator: user.translator, locales: user.translator_locales })
  const toggle = (code: string) =>
    form.setData("locales", form.data.locales.includes(code) ? form.data.locales.filter((c) => c !== code) : [...form.data.locales, code])

  return (
    <form
      className="mt-10 max-w-md space-y-3"
      aria-label="Translator"
      onSubmit={(e) => {
        e.preventDefault()
        form.patch(`/admin/users/${user.id}/translator`, { preserveScroll: true })
      }}
    >
      <h2>Translator</h2>
      <p className="text-sm text-ink-muted">
        Translators can use the translation editor for the languages below, and nothing else in the admin area.
      </p>
      <label className="flex items-center gap-2 font-normal text-ink-body">
        <Checkbox checked={form.data.translator} onChange={(e) => form.setData("translator", e.target.checked)} />
        Can translate
      </label>
      {form.data.translator && (
        <fieldset className="space-y-1">
          <legend>Languages</legend>
          {TRANSLATABLE.map((l) => (
            <label key={l.code} className="flex items-center gap-2 font-normal text-ink-body">
              <Checkbox checked={form.data.locales.includes(l.code)} onChange={() => toggle(l.code)} />
              {l.name}
            </label>
          ))}
        </fieldset>
      )}
      <Button type="submit" disabled={form.processing}>
        Save translator access
      </Button>
    </form>
  )
}
